import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  SalesState,
  CreateSalesOrderPayload,
  SalesOrder,
} from "src/types/sales.types";

const initialState: SalesState = {
  salesList: {
    loading: false,
    data: null,
    error: null,
  },
  createSaleOrder: {
    loading: false,
    data: null,
    error: null,
  },
  editSaleOrder: {
    loading: false,
    data: null,
    error: null,
  },
  completeSaleOrder: {
    loading: false,
    data: null,
    error: null,
  },
  cancelSaleOrder: {
    loading: false,
    data: null,
    error: null,
  },
};

const salesSlice = createSlice({
  name: "sales",
  initialState,
  reducers: {
    // 📋 List Sales Orders
    getSalesListRequest: (
      state,
      action: PayloadAction<{
        page: number;
        limit: number;
        createdBy?: string;
        customerId?: string;
        search?: string;
        orderStatus?: "pending" | "completed" | "cancelled";
        paymentStatus?:
          | "unpaid"
          | "partially_paid"
          | "paid"
          | "link_expired"
          | "cancelled";
      }>
    ) => {
      state.salesList.loading = true;
      state.salesList.error = null;
    },
    getSalesListSuccess: (state, action: PayloadAction<any>) => {
      state.salesList.loading = false;
      state.salesList.data = action.payload;
    },
    getSalesListError: (state, action: PayloadAction<string>) => {
      state.salesList.loading = false;
      state.salesList.error = action.payload;
    },

    // 💰 Create Sales Order
    createSalesOrderRequest: (
      state,
      action: PayloadAction<CreateSalesOrderPayload>
    ) => {
      state.createSaleOrder.loading = true;
      state.createSaleOrder.error = null;
    },
    createSalesOrderSuccess: (state, action: PayloadAction<SalesOrder>) => {
      state.createSaleOrder.loading = false;
      state.createSaleOrder.data = action.payload;
    },
    createSalesOrderError: (state, action: PayloadAction<string>) => {
      state.createSaleOrder.loading = false;
      state.createSaleOrder.error = action.payload;
    },

    // ✏️ Edit Sales Order
    editSalesOrderRequest: (
      state,
      _action: PayloadAction<{ id: string; data: CreateSalesOrderPayload }>
    ) => {
      state.editSaleOrder.loading = true;
      state.editSaleOrder.error = null;
    },
    editSalesOrderSuccess: (state, action: PayloadAction<SalesOrder>) => {
      state.editSaleOrder.loading = false;
      state.editSaleOrder.data = action.payload;
    },
    editSalesOrderError: (state, action: PayloadAction<string>) => {
      state.editSaleOrder.loading = false;
      state.editSaleOrder.error = action.payload;
    },

    // ✅ Complete Sales Order
    completeSalesOrderRequest: (state, _action: PayloadAction<string>) => {
      state.completeSaleOrder.loading = true;
      state.completeSaleOrder.error = null;
    },
    completeSalesOrderSuccess: (state, action: PayloadAction<SalesOrder>) => {
      state.completeSaleOrder.loading = false;
      state.completeSaleOrder.data = action.payload;
    },
    completeSalesOrderError: (state, action: PayloadAction<string>) => {
      state.completeSaleOrder.loading = false;
      state.completeSaleOrder.error = action.payload;
    },

    // 🚫 Cancel Sales Order
    cancelSalesOrderRequest: (state, _action: PayloadAction<string>) => {
      state.cancelSaleOrder.loading = true;
      state.cancelSaleOrder.error = null;
    },
    cancelSalesOrderSuccess: (state, action: PayloadAction<SalesOrder>) => {
      state.cancelSaleOrder.loading = false;
      state.cancelSaleOrder.data = action.payload;
    },
    cancelSalesOrderError: (state, action: PayloadAction<string>) => {
      state.cancelSaleOrder.loading = false;
      state.cancelSaleOrder.error = action.payload;
    },

    resetSalesList: (state) => {
      state.salesList = { loading: false, data: null, error: null };
    },
    resetCreateSalesOrder: (state) => {
      state.createSaleOrder = { loading: false, data: null, error: null };
    },
    resetEditSalesOrder: (state) => {
      state.editSaleOrder = { loading: false, data: null, error: null };
    },
    resetCompleteSalesOrder: (state) => {
      state.completeSaleOrder = { loading: false, data: null, error: null };
    },
    resetCancelSalesOrder: (state) => {
      state.cancelSaleOrder = { loading: false, data: null, error: null };
    },
  },
});

export const {
  getSalesListRequest,
  getSalesListSuccess,
  getSalesListError,
  createSalesOrderRequest,
  createSalesOrderSuccess,
  createSalesOrderError,
  editSalesOrderRequest,
  editSalesOrderSuccess,
  editSalesOrderError,
  completeSalesOrderRequest,
  completeSalesOrderSuccess,
  completeSalesOrderError,
  cancelSalesOrderRequest,
  cancelSalesOrderSuccess,
  cancelSalesOrderError,
  resetSalesList,
  resetCreateSalesOrder,
  resetEditSalesOrder,
  resetCompleteSalesOrder,
  resetCancelSalesOrder,
} = salesSlice.actions;

export default salesSlice.reducer;
